import type { Theme } from '../types/theme';
import type { Locale } from '../i18n';
import { downloadFile, slugify } from './download';
import { getAccessibleAccentColor, getAccessibleTextColor, isDarkColor } from './color';

type PageType = 'landing' | 'dashboard';
type ToolTarget = 'generic' | 'claude-code' | 'cursor' | 'google-stitch' | 'v0' | 'lovable' | 'bolt';

export function generateDesignMd(theme: Theme, pageType: 'landing' | 'dashboard'): string {
  const { colors, typography, radius, layout } = theme;
  const isDark = isDarkColor(colors.background);
  const accessiblePrimaryText = getAccessibleTextColor(colors.onPrimary, colors.primary);
  const accessibleBackgroundAccent = getAccessibleAccentColor(colors.primary, colors.background, colors.primaryHover);
  const accessibleSurfaceAccent = getAccessibleAccentColor(colors.primary, colors.surface, colors.primaryHover);
  const accessibleSurfaceText = getAccessibleTextColor(colors.textPrimary, colors.surface);

  const pageTypeDesc = pageType === 'landing'
    ? 'AI product landing page with hero, features, pricing, and CTA sections.'
    : 'Data dashboard with sidebar navigation, stats cards, charts, and data tables.';

  return `---
version: alpha
name: ${theme.name}
description: ${theme.description}
page-type: ${pageType}
---

# ${theme.name} Design System

${theme.description}

Page Type: ${pageTypeDesc}

## 1. Visual Theme & Atmosphere

**Design Philosophy**: ${theme.mood.tone}

**Visual Keywords**: ${theme.visualKeywords.join(', ')}

**Density**: ${theme.mood.density}

## 2. Color Palette & Roles

### Primary Colors
| Token | Hex | Usage |
|-------|-----|-------|
| Primary | ${colors.primary} | Primary CTA, brand accent |
| Primary Hover | ${colors.primaryHover} | Hover state |
| Accent | ${colors.accent} | Secondary accent |

### Surface Colors
| Token | Hex | Usage |
|-------|-----|-------|
| Background | ${colors.background} | Page background |
| Surface | ${colors.surface} | Cards, panels |
| Surface Elevated | ${colors.surfaceElevated} | Elevated elements |

### Text Colors
| Token | Hex | Usage |
|-------|-----|-------|
| Text Primary | ${colors.textPrimary} | Headlines, body |
| Text Secondary | ${colors.textSecondary} | Descriptions |
| Text Muted | ${colors.textMuted} | Captions, meta |

### Border Colors
| Token | Hex | Usage |
|-------|-----|-------|
| Border | ${colors.border} | Dividers, outlines |
| Border Subtle | ${colors.borderSubtle || colors.border} | Subtle dividers |

### Accessibility-safe Rendering
| Role | Recommended Value | Usage |
|------|-------------------|-------|
| Primary Button Text | ${accessiblePrimaryText} | Use on Primary when source onPrimary fails AA contrast |
| Accent on Background | ${accessibleBackgroundAccent} | Text links, active states, outline buttons on page background |
| Accent on Surface | ${accessibleSurfaceAccent} | Text links, active states, status accents on cards and panels |
| Primary Text on Surface | ${accessibleSurfaceText} | Panel headings and dense UI labels |

## 3. Typography

| Property | Value |
|----------|-------|
| Heading Font | ${typography.headingFont} |
| Body Font | ${typography.bodyFont} |
| Mono Font | ${typography.monoFont || 'N/A'} |
| Heading Weight | ${typography.headingWeight} |
| Body Weight | ${typography.bodyWeight} |
| Heading Letter Spacing | ${typography.letterSpacingHeading} |

## 4. Component Styling

### Buttons
- **Primary Button**: Background ${colors.primary}, text ${accessiblePrimaryText}, rounded ${radius.button}
- **Secondary Button**: Border ${colors.border}, text ${accessibleBackgroundAccent}, rounded ${radius.button}
- **Touch Target**: Minimum height 44px for interactive controls

### Cards
- **Background**: ${colors.surface}
- **Border**: 1px solid ${colors.border}
- **Radius**: ${radius.card}
- **Padding**: ${layout.cardPadding}
- **Shadow**: ${isDark ? 'none or restrained shadow (use borders for depth)' : 'subtle shadow'}

### Navigation
- **Height**: ${layout.navHeight}
- **Background**: ${colors.background}
- **Border Bottom**: 1px solid ${colors.border}

## 5. Layout

| Property | Value |
|----------|-------|
| Max Width | ${layout.maxWidth} |
| Section Spacing | ${layout.sectionSpacing} |
| Card Padding | ${layout.cardPadding} |

## 6. Border Radius Scale

| Element | Radius |
|---------|--------|
| Button | ${radius.button} |
| Card | ${radius.card} |
| Panel | ${radius.panel} |
| Badge | ${radius.badge} |
| Input | ${radius.input || radius.button} |

## 7. Best For

${theme.mood.bestFor.map(item => `- ${item}`).join('\n')}

## 8. Not Good For

${theme.mood.notGoodFor.map(item => `- ${item}`).join('\n')}

## 9. Usage Example

\`\`\`html
<!-- Primary Button -->
<button style="
  background-color: ${colors.primary};
  color: ${accessiblePrimaryText};
  border-radius: ${radius.button};
  padding: 12px 24px;
  min-height: 44px;
">
  Get Started
</button>

<!-- Card -->
<div style="
  background-color: ${colors.surface};
  border: 1px solid ${colors.border};
  border-radius: ${radius.card};
  padding: ${layout.cardPadding};
">
  Card Content
</div>
\`\`\`

---

*Generated from VibeUI - ${new Date().toISOString().split('T')[0]}*
`;
}

export function exportTheme(theme: Theme, pageType: 'landing' | 'dashboard' = 'landing'): void {
  const content = generateDesignMd(theme, pageType);
  const filename = `${slugify(theme.name)}-design.md`;
  downloadFile(content, filename, 'text/markdown');
}

export function generateToolPrompt(
  theme: Theme,
  pageType: PageType = 'landing',
  locale: Locale = 'en',
  toolTarget: ToolTarget = 'generic',
): string {
  const pageSurface = pageType === 'landing'
    ? locale === 'zh' ? '落地页' : 'landing page'
    : locale === 'zh' ? '仪表盘' : 'dashboard';
  const toolName = getToolName(toolTarget);
  const { colors, typography, radius, layout } = theme;
  const accessiblePrimaryText = getAccessibleTextColor(colors.onPrimary, colors.primary);
  const accessibleAccent = getAccessibleAccentColor(colors.primary, colors.background, colors.primaryHover);

  if (locale === 'zh') {
    return `请在 ${toolName} 中使用下面的视觉方向生成或改造一个${pageSurface}。不要照搬任何品牌资产、logo 或商标，只借鉴视觉系统的抽象特征。

风格名称：${theme.name}
风格描述：${theme.description}
视觉关键词：${theme.visualKeywords.join('、')}
适合场景：${theme.mood.bestFor.join('、')}
不适合场景：${theme.mood.notGoodFor.join('、')}
整体语气：${theme.mood.tone}
信息密度：${theme.mood.density}

颜色角色：
- 背景：${colors.background}
- 表面：${colors.surface}
- 抬升表面：${colors.surfaceElevated}
- 主文字：${colors.textPrimary}
- 次级文字：${colors.textSecondary}
- 边框：${colors.border}
- 主操作：${colors.primary}
- 主操作文字：${accessiblePrimaryText}
- 链接/强调色：${accessibleAccent}

排版与组件：
- 标题字体：${typography.headingFont}
- 正文字体：${typography.bodyFont}
- 标题字重：${typography.headingWeight}
- 正文字重：${typography.bodyWeight}
- 按钮圆角：${radius.button}
- 卡片圆角：${radius.card}
- 面板圆角：${radius.panel}
- 最大宽度：${layout.maxWidth}
- 区块间距：${layout.sectionSpacing}

执行要求：
- 保持同一套组件语言，不要混用多种按钮、卡片和输入框风格。
- 优先保证 WCAG AA 可读性，原始颜色不满足对比度时使用上面的安全文字色。
- 让风格服务于产品判断，不要添加与页面目标无关的装饰。
- 不要生成完整品牌系统，只生成当前页面所需的 UI。`;
  }

  return `Use this visual direction in ${toolName} to generate or adapt a ${pageSurface}. Do not copy brand assets, logos, or trademarks. Borrow only the abstract visual system.

Style: ${theme.name}
Description: ${theme.description}
Visual keywords: ${theme.visualKeywords.join(', ')}
Best for: ${theme.mood.bestFor.join(', ')}
Not good for: ${theme.mood.notGoodFor.join(', ')}
Tone: ${theme.mood.tone}
Density: ${theme.mood.density}

Color roles:
- Background: ${colors.background}
- Surface: ${colors.surface}
- Elevated surface: ${colors.surfaceElevated}
- Primary text: ${colors.textPrimary}
- Secondary text: ${colors.textSecondary}
- Border: ${colors.border}
- Primary action: ${colors.primary}
- Primary action text: ${accessiblePrimaryText}
- Link/accent: ${accessibleAccent}

Typography and components:
- Heading font: ${typography.headingFont}
- Body font: ${typography.bodyFont}
- Heading weight: ${typography.headingWeight}
- Body weight: ${typography.bodyWeight}
- Button radius: ${radius.button}
- Card radius: ${radius.card}
- Panel radius: ${radius.panel}
- Max width: ${layout.maxWidth}
- Section spacing: ${layout.sectionSpacing}

Execution notes:
- Keep one consistent component vocabulary across buttons, cards, inputs, and navigation.
- Preserve WCAG AA readability. If raw colors fail contrast, use the safe text colors above.
- Let the style serve product evaluation. Avoid decoration that does not support the page goal.
- Do not generate a complete brand system. Generate only the UI needed for this page.`;
}

export function downloadToolPrompt(theme: Theme, pageType: PageType = 'landing', locale: Locale = 'en'): void {
  const content = generateToolPrompt(theme, pageType, locale);
  const filename = `${slugify(theme.name)}-${pageType}-prompt.txt`;
  downloadFile(content, filename, 'text/plain');
}

function getToolName(toolTarget: ToolTarget): string {
  const labels: Record<ToolTarget, string> = {
    generic: 'your AI UI tool',
    'claude-code': 'Claude Code',
    cursor: 'Cursor',
    'google-stitch': 'Google Stitch',
    v0: 'v0',
    lovable: 'Lovable',
    bolt: 'Bolt',
  };

  return labels[toolTarget];
}
